import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router-dom";

import { AppShell } from "./components/AppShell";
import { session, useSession } from "./lib/session";
import { HomePage } from "./pages/HomePage";
import { AgencyPage } from "./pages/AgencyPage";
import { AgendaPage } from "./pages/AgendaPage";
import { AiTestPage } from "./pages/AiTestPage";
import { BiblePage } from "./pages/BiblePage";
import { BrandPage } from "./pages/BrandPage";
import { CatalogPage } from "./pages/CatalogPage";
import { ClientPage } from "./pages/ClientPage";
import { CrmPage } from "./pages/CrmPage";
import { DocumentsPage } from "./pages/DocumentsPage";
import { DossierPage } from "./pages/DossierPage";
import { DossiersPage } from "./pages/DossiersPage";
import { InboxPage } from "./pages/InboxPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { LoginPage } from "./pages/LoginPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { PackageEditorPage } from "./pages/PackageEditorPage";
import { ServiceEditorPage } from "./pages/ServiceEditorPage";
import { ReceptionPage } from "./pages/ReceptionPage";
import { InvitationPage } from "./pages/InvitationPage";
import { AcademyApp } from "./academy/AcademyApp";
import { AcademyAdminPage } from "./pages/academy/AcademyAdminPage";
import { AuditPage } from "./pages/AuditPage";
import { GrowthPage } from "./pages/GrowthPage";
import { TeamPage } from "./pages/TeamPage";
import { ContractsPage } from "./pages/ContractsPage";
import { ContractPage } from "./pages/ContractPage";
import { ContractTemplatesPage } from "./pages/ContractTemplatesPage";
import { SignPage } from "./pages/SignPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { InvoicePage } from "./pages/InvoicePage";
import { CashDeskPage } from "./pages/CashDeskPage";
import { BillingSettingsPage } from "./pages/BillingSettingsPage";
import { PayPage } from "./pages/PayPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { WebsitePage } from "./pages/WebsitePage";
import { StudioPage } from "./pages/studio/StudioPage";
import { AcademyMarketingPage } from "./pages/academyMarketing/AcademyMarketingPage";
import { ExamDatesPage } from "./pages/examDates/ExamDatesPage";
import { AdsPage } from "./pages/ads/AdsPage";
import { AdCampaignPage } from "./pages/ads/AdCampaignPage";
import { SocialPage } from "./pages/social/SocialPage";
import { DesignPage } from "./pages/design/DesignPage";
import { CustomerCardPage } from "./pages/support/CustomerCardPage";
import { CsatPublicPage } from "./pages/support/Rating";
import { SupportPage } from "./pages/support/SupportPage";
import { TicketPage } from "./pages/support/TicketPage";
import { CallsPage } from "./pages/calls/CallsPage";
import { OnboardingPage } from "./pages/onboarding/OnboardingPage";
import { PlatformAgenciesPage } from "./pages/onboarding/PlatformAgenciesPage";
import { SubscriptionPage } from "./pages/saas/SubscriptionPage";

export function createQueryClient({ retry = 1 }: { retry?: number | false } = {}): QueryClient {
  return new QueryClient({
    defaultOptions: {
      // Slow 4G: keep data for a while, retry once, do not refetch on every focus.
      // Offline, queries and changes pause and resume by themselves when the network is back.
      queries: { retry, staleTime: 30_000, refetchOnWindowFocus: false },
    },
  });
}

function RequireSession({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { accessToken, ready } = useSession();
  useEffect(() => {
    if (!ready && !accessToken) void session.restore();
  }, [ready, accessToken]);
  if (accessToken) return children;
  if (!ready) return <p className="app-splash">{t("common.loading")}</p>;
  return <Navigate to="/login" replace />;
}

export function App({ queryClient = createQueryClient() }: { queryClient?: QueryClient }) {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invitation/:token" element={<InvitationPage />} />
        <Route path="/academy/*" element={<AcademyApp />} />
        <Route path="/sign/:token" element={<SignPage />} />
        <Route path="/pay/:token" element={<PayPage />} />
        <Route path="/support/csat/:token" element={<CsatPublicPage />} />
        <Route
          element={
            <RequireSession>
              <AppShell />
            </RequireSession>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="inbox" element={<InboxPage />} />
          <Route path="calls" element={<CallsPage />} />
          <Route path="agenda" element={<AgendaPage />} />
          <Route path="reception" element={<ReceptionPage />} />
          <Route path="ai-test" element={<AiTestPage />} />
          <Route path="leads" element={<CrmPage />} />
          <Route path="clients/:id" element={<ClientPage />} />
          <Route path="dossiers" element={<DossiersPage />} />
          <Route path="contracts" element={<ContractsPage />} />
          <Route path="contracts/:id" element={<ContractPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="invoices/:id" element={<InvoicePage />} />
          <Route path="cash" element={<CashDeskPage />} />
          <Route path="dossiers/:id" element={<DossierPage />} />
          <Route path="bible" element={<BiblePage />} />
          <Route path="catalog" element={<CatalogPage />} />
          <Route path="website" element={<WebsitePage />} />
          <Route path="studio" element={<StudioPage />} />
          <Route path="ads" element={<AdsPage />} />
          <Route path="ads/:id" element={<AdCampaignPage />} />
          <Route path="academy-marketing" element={<AcademyMarketingPage />} />
          <Route path="social" element={<SocialPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="support/tickets/:id" element={<TicketPage />} />
          <Route path="support/customers/:id" element={<CustomerCardPage kind="customers" />} />
          <Route path="support/learners/:id" element={<CustomerCardPage kind="learners" />} />
          {/* Hidden reference page of the design system v2 (D-104), not in the menu. */}
          <Route path="design" element={<DesignPage />} />
          <Route path="academy-admin/*" element={<AcademyAdminPage />} />
          <Route path="exam-dates" element={<ExamDatesPage />} />
          <Route path="catalog/services/:id" element={<ServiceEditorPage />} />
          <Route path="catalog/packages/:id" element={<PackageEditorPage />} />
          <Route path="settings" element={<Navigate to="/settings/agency" replace />} />
          <Route path="settings/agency" element={<AgencyPage />} />
          <Route path="settings/brand" element={<BrandPage />} />
          <Route path="settings/documents" element={<DocumentsPage />} />
          <Route path="settings/integrations" element={<IntegrationsPage />} />
          <Route path="settings/payments" element={<PaymentsPage />} />
          <Route path="settings/team" element={<TeamPage />} />
          <Route path="settings/growth" element={<GrowthPage />} />
          <Route path="settings/contracts" element={<ContractTemplatesPage />} />
          <Route path="settings/billing" element={<BillingSettingsPage />} />
          <Route path="settings/notifications" element={<NotificationsPage />} />
          <Route path="settings/audit" element={<AuditPage />} />
          <Route path="onboarding" element={<OnboardingPage />} />
          <Route path="platform/agencies" element={<PlatformAgenciesPage />} />
          <Route path="settings/subscription" element={<SubscriptionPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </QueryClientProvider>
  );
}
